/**
 * Flota, etap 2 — panel WWW (`fleet.web.*`): tankowania, koszty, zgłoszenia, checklisty, szablony, serwisy.
 * Doklejone do `fleet.web` w `extra.ts` obok `fleet.ts` (etap 1). Błędy API: `fleet.errors.*` w `@nexops/shared`.
 * `fleetReportsEn` jest typowany względem `fleetReportsPl` — brak klucza = błąd kompilacji.
 */
import { fleetReportsPlOps } from './fleet-reports-pl-ops';

export type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetReportsPl = {
  nav: { fuel: 'Tankowania', expenses: 'Koszty', incidents: 'Zgłoszenia', checklists: 'Checklisty', service: 'Serwisy', templates: 'Szablony', dashboard: 'Pulpit floty', analytics: 'Analityka', routes: 'Trasy', integration: 'Integracja tras' },
  enums: {
    reviewStatus: { SUBMITTED: 'Do weryfikacji', APPROVED: 'Zatwierdzone', REJECTED: 'Odrzucone', NEEDS_INFO: 'Do uzupełnienia' },
    reviewEvent: { create: 'Dodano wpis', approve: 'Zatwierdzono', reject: 'Odrzucono', 'request-info': 'Poproszono o uzupełnienie', resubmit: 'Uzupełniono i wysłano ponownie' },
    reviewAction: { approve: 'Zatwierdź', reject: 'Odrzuć', 'request-info': 'Poproś o uzupełnienie' },
    fuelType: { DIESEL: 'Diesel', PETROL: 'Benzyna', LPG: 'LPG', CNG: 'CNG', ELECTRIC: 'Prąd', ADBLUE: 'AdBlue', OTHER: 'Inne' },
    paymentMethod: { FUEL_CARD: 'Karta paliwowa', COMPANY_CARD: 'Karta firmowa', CASH: 'Gotówka', PRIVATE: 'Środki prywatne', OTHER: 'Inne' },
    fuelSource: { MANUAL: 'Wpis kierowcy', CARD_IMPORT: 'Import z karty' },
    fuelWarning: {
      ODOMETER_BELOW_PREVIOUS: 'Licznik niższy niż poprzednio',
      ODOMETER_MISSING: 'Brak stanu licznika',
      AMOUNT_MISMATCH: 'Wartość ≠ litry × cena',
      UNUSUAL_PRICE: 'Nietypowa cena',
      LITERS_ABOVE_CAPACITY: 'Więcej niż pojemność baku',
      DUPLICATE_SUSPECTED: 'Możliwy duplikat',
    },
    fuelWarningHelp: {
      ODOMETER_BELOW_PREVIOUS: 'Podany stan licznika jest niższy niż poprzedni odczyt tego pojazdu. Odczyt nie został zapisany i licznik pojazdu się nie zmienił — sprawdź paragon i poproś o uzupełnienie albo popraw dane.',
      ODOMETER_MISSING: 'Wpis nie ma stanu licznika, więc nie powstał odczyt i nie da się policzyć spalania.',
      AMOUNT_MISMATCH: 'Wartość tankowania nie zgadza się z iloczynem litrów i ceny za litr (poza tolerancją 1% lub 0,05). Porównaj ze zdjęciem paragonu.',
      UNUSUAL_PRICE: 'Cena za litr odbiega o ponad 40% od mediany ostatnich tankowań tego paliwa (albo jest poza rozsądnym zakresem).',
      LITERS_ABOVE_CAPACITY: 'Zatankowano więcej litrów, niż wynosi pojemność baku pojazdu. Może to być błąd w danych albo tankowanie do kanistra.',
      DUPLICATE_SUSPECTED: 'W ciągu 2 godzin zapisano inne tankowanie tego pojazdu z tą samą liczbą litrów — sprawdź, czy to nie to samo tankowanie wpisane dwa razy.',
    },
    expenseCategory: { PARKING: 'Parking', TOLL: 'Opłaty drogowe', CAR_WASH: 'Mycie', MINOR_REPAIR: 'Drobna naprawa', FLUIDS: 'Płyny eksploatacyjne', OTHER: 'Inne' },
    incidentType: { BREAKDOWN: 'Awaria', DAMAGE: 'Uszkodzenie', COLLISION: 'Kolizja', IRREGULARITY: 'Nieprawidłowość', OTHER: 'Inne' },
    severity: { LOW: 'Niska', MEDIUM: 'Średnia', HIGH: 'Wysoka', CRITICAL: 'Krytyczna' },
    incidentStatus: { NEW: 'Nowe', IN_PROGRESS: 'W toku', RESOLVED: 'Rozwiązane', CLOSED: 'Zamknięte' },
    incidentStatusAction: { NEW: 'Oznacz jako nowe', IN_PROGRESS: 'Podejmij (w toku)', RESOLVED: 'Oznacz jako rozwiązane', CLOSED: 'Zamknij zgłoszenie' },
    decision: { NONE: 'Bez decyzji', PENDING: 'Czeka na decyzję', ALLOWED: 'Dopuszczony do jazdy', BLOCKED: 'Zablokowany' },
    incidentEvent: {
      CREATED: 'Zgłoszono',
      STATUS_CHANGED: 'Zmiana statusu',
      COMMENT: 'Komentarz',
      DECISION: 'Decyzja o pojeździe',
      VEHICLE_BLOCKED: 'Pojazd zablokowany automatycznie',
      UPDATED: 'Zmiana danych zgłoszenia',
    },
    checklistPhase: { BEFORE_TRIP: 'Przed jazdą', AFTER_TRIP: 'Po jeździe' },
    checklistResult: { OK: 'W porządku', WARNING: 'Uwaga', DEFECT: 'Usterka' },
    requirement: { NEVER: 'Nie wymagane', ON_ISSUE: 'Przy uwadze lub usterce', ALWAYS: 'Zawsze' },
    serviceType: { INSPECTION: 'Przegląd', SERVICE: 'Serwis', REPAIR: 'Naprawa', TIRES: 'Opony', DEFECT: 'Usterka', OTHER: 'Inne' },
    serviceStatus: { PLANNED: 'Zaplanowany', IN_PROGRESS: 'W trakcie', DONE: 'Wykonany' },
    fieldType: { text: 'Tekst', number: 'Liczba', boolean: 'Tak / nie', select: 'Lista wyboru', date: 'Data' },
    defectProcedure: {
      NOTIFY_ONLY: 'Tylko powiadomienie',
      BLOCK_VEHICLE: 'Automatycznie zablokuj pojazd',
      REQUIRE_DECISION: 'Wymagaj decyzji uprawnionej osoby',
    },
    defectProcedureHelp: {
      NOTIFY_ONLY: 'Osoby obsługujące zgłoszenia dostają powiadomienie, pojazd może dalej jeździć.',
      BLOCK_VEHICLE: 'Pojazd od razu przechodzi w status „W serwisie”; zwolnienie po naprawie to zmiana statusu pojazdu.',
      REQUIRE_DECISION: 'Pojazd nie może rozpocząć nowej karty, dopóki ktoś z uprawnieniem nie zdecyduje: dopuścić do jazdy albo zablokować.',
    },
  },
  common: {
    all: 'Wszystkie',
    allVehicles: 'Wszystkie pojazdy',
    allDrivers: 'Wszyscy kierowcy',
    allStatuses: 'Wszystkie statusy',
    filterVehicle: 'Pojazd',
    filterDriver: 'Kierowca',
    filterStatus: 'Status',
    filterPeriod: 'Okres (miesiąc)',
    comment: 'Komentarz',
    optional: 'opcjonalnie',
    history: 'Historia',
    details: 'Szczegóły',
    noHistory: 'Brak wpisów w historii.',
    back: 'Wróć do listy',
    openVehicle: 'Karta pojazdu',
    openLogbook: 'Karta drogowa',
    driver: 'Kierowca',
    vehicle: 'Pojazd',
    date: 'Data',
    status: 'Status',
    amount: 'Kwota',
    reviewedBy: 'Zweryfikował: {name}',
    notFoundHint: 'Wpis nie istnieje albo nie masz do niego dostępu.',
    saved: 'Zapisano.',
    add: 'Dodaj',
    edit: 'Edytuj',
    delete: 'Usuń',
    yes: 'Tak',
    no: 'Nie',
    loadMore: 'Pokaż wszystkie',
    viewAll: 'Zobacz wszystkie',
    none: 'Brak',
    unknown: 'nieznany',
  },
  attach: {
    title: 'Załączniki',
    upload: 'Dodaj plik',
    uploaded: 'Dodano załącznik.',
    deleted: 'Usunięto załącznik.',
    empty: 'Brak załączników.',
    deleteConfirm: 'Usunąć załącznik „{name}”?',
    download: 'Pobierz {name}',
    delete: 'Usuń {name}',
    preview: 'Podgląd: {name}',
    missing: 'Brakuje załączników: {count}',
  },
  review: {
    fuel: {
      title: 'Tankowania',
      description: 'Kolejka weryfikacji tankowań: sprawdź paragon i ostrzeżenia, potem zatwierdź, odrzuć albo poproś kierowcę o uzupełnienie.',
      search: 'Szukaj po stacji, rejestracji, kierowcy…',
      chipTotal: 'Tankowania',
      chipWarned: 'Z ostrzeżeniami',
      empty: 'Brak tankowań do pokazania.',
      emptyFiltered: 'Żadne tankowanie nie pasuje do filtrów.',
      onlyWarned: 'Tylko z ostrzeżeniami',
      filterType: 'Rodzaj paliwa',
      allTypes: 'Wszystkie paliwa',
      filterSource: 'Źródło',
      allSources: 'Wszystkie źródła',
      col: { date: 'Data', vehicle: 'Pojazd', driver: 'Kierowca', fuel: 'Paliwo', liters: 'Litry', amount: 'Kwota', odometer: 'Licznik', warnings: 'Ostrzeżenia', receipt: 'Paragon', status: 'Status' },
      fields: {
        filledAt: 'Data tankowania',
        fuelType: 'Rodzaj paliwa',
        liters: 'Litry',
        unitPrice: 'Cena za litr',
        total: 'Wartość',
        odometer: 'Stan licznika',
        station: 'Stacja',
        payment: 'Płatność',
        fuelCard: 'Karta paliwowa',
        fullTank: 'Do pełna',
        source: 'Źródło',
        comment: 'Uwagi kierowcy',
      },
      fullTankYes: 'tak',
      fullTankNo: 'nie',
      warningsTitle: 'Ostrzeżenia do sprawdzenia',
      warningsHint: 'Ostrzeżenia nie blokują zapisu — pomagają wychwycić błędy i nadużycia przed zatwierdzeniem.',
      noWarnings: 'Brak ostrzeżeń.',
      receiptTitle: 'Zdjęcie paragonu',
      receiptMissing: 'Brak zdjęcia paragonu.',
      receiptRequired: 'Zatwierdzenie wymaga zdjęcia paragonu (ustawienie floty).',
      correct: 'Popraw dane',
      correctTitle: 'Korekta tankowania',
      correctDescription: 'Popraw dane po sprawdzeniu paragonu. Zmiana trafia do audytu, a wpis wraca do ponownych ostrzeżeń.',
      correctSubmit: 'Zapisz korekty',
      corrected: 'Zapisano korektę tankowania.',
      errLiters: 'Litry: liczba od 0,01 do 2000.',
      errAmount: 'Wartość: liczba od 0 do 1 000 000.',
      errOdometer: 'Stan licznika: liczba całkowita od 0.',
      odometerLocked: 'Stan licznika nie może być zmieniony, bo na jego podstawie powstał już odczyt.',
    },
    expenses: {
      title: 'Koszty pojazdów',
      description: 'Kolejka weryfikacji drobnych kosztów zgłoszonych przez kierowców: parkingi, opłaty drogowe, mycie, płyny.',
      search: 'Szukaj po opisie, rejestracji, kierowcy…',
      chipTotal: 'Koszty',
      empty: 'Brak kosztów do pokazania.',
      emptyFiltered: 'Żaden koszt nie pasuje do filtrów.',
      filterCategory: 'Kategoria',
      allCategories: 'Wszystkie kategorie',
      col: { date: 'Data', vehicle: 'Pojazd', driver: 'Kierowca', category: 'Kategoria', amount: 'Kwota', description: 'Opis', documents: 'Dokumenty', status: 'Status' },
      fields: { incurredAt: 'Data poniesienia', category: 'Kategoria', amount: 'Kwota', description: 'Opis' },
      documentsTitle: 'Dokumenty i zdjęcia',
    },
    selfReviewHint: 'To Twój własny wpis — weryfikować go może tylko inna osoba.',
    noActions: 'Ten wpis jest zamknięty — nie ma dostępnych akcji.',
    awaitingAuthor: 'Czeka na uzupełnienie przez kierowcę.',
    dialog: {
      approve: 'Zatwierdzić ten wpis? Komentarz jest opcjonalny.',
      reject: 'Odrzucenie jest ostateczne — kierowca dostanie powiadomienie z komentarzem.',
      'request-info': 'Kierowca dostanie powiadomienie i będzie mógł uzupełnić wpis. Opisz, czego brakuje.',
    },
    commentRequired: 'Komentarz jest wymagany przy tej akcji.',
    done: { approve: 'Wpis zatwierdzony.', reject: 'Wpis odrzucony.', 'request-info': 'Poproszono kierowcę o uzupełnienie.' },
  },
  ...fleetReportsPlOps,
};
